import axios from 'axios';

/**
 * Thrown when a caller invokes `open()` on ContentEditorService or
 * InlineCommentManager while another content is already open. The services
 * enforce a single open content at a time — the caller must call
 * `closeAndDiscard()` (or `closeAndSave()`) first.
 */
export class ContentAlreadyOpenError extends Error {
  constructor(
    readonly openContentId: string,
    readonly requestedContentId: string,
  ) {
    super(
      `Cannot open content "${requestedContentId}": content "${openContentId}" is already open. ` +
        `Call closeAndDiscard() or closeAndSave() first.`,
    );
    this.name = 'ContentAlreadyOpenError';
  }
}

/**
 * Thrown when a caller invokes a method that requires an open content
 * (e.g. `recordEdit`, `save`, `refresh`) while nothing is open.
 */
export class NoContentOpenError extends Error {
  constructor(method: string) {
    super(`Cannot call ${method}: no content is open. Call open() first.`);
    this.name = 'NoContentOpenError';
  }
}

/**
 * Thrown when persisting a working copy to the backend fails. Wraps the
 * underlying network/server error so callers can distinguish a save
 * failure from unrelated programming errors.
 */
export class SaveWorkingCopyError extends Error {
  constructor(
    readonly cause: unknown,
    message = 'Failed to save working copy.',
  ) {
    super(message);
    this.name = 'SaveWorkingCopyError';
  }

  /** Build from a classified backend failure, composing the operation with why it failed. */
  static fromServiceCall(cause: ServiceCallError): SaveWorkingCopyError {
    return new SaveWorkingCopyError(cause, `Failed to save your changes: ${cause.message}`);
  }
}

/**
 * Thrown when promoting staged edits to the real draft/finalizing content
 * fails. Wraps the underlying error; callers pass a message describing
 * which step failed so it can be surfaced in the UI.
 */
export class SaveDraftError extends Error {
  constructor(
    message: string,
    readonly cause: unknown,
  ) {
    super(message);
    this.name = 'SaveDraftError';
  }

  /** Build from a classified backend failure, appending why it failed to the operation message. */
  static fromServiceCall(cause: ServiceCallError, message: string): SaveDraftError {
    return new SaveDraftError(`${message}: ${cause.message}`, cause);
  }
}

/**
 * Thrown when persisting inline comments to the server fails. Wraps the
 * underlying network/server error so callers can distinguish a comment-save
 * failure from unrelated programming errors.
 */
export class SaveCommentError extends Error {
  constructor(
    readonly cause: unknown,
    message = 'Failed to save comments.',
  ) {
    super(message);
    this.name = 'SaveCommentError';
  }

  /** Build from a classified backend failure, composing the operation with why it failed. */
  static fromServiceCall(cause: ServiceCallError): SaveCommentError {
    return new SaveCommentError(cause, `Failed to save comments: ${cause.message}`);
  }
}

/**
 * Thrown when accessing user identity on UserService before a profile has
 * been set — i.e. before the user has logged in.
 */
export class UserNotLoggedInError extends Error {
  constructor() {
    super('No user is logged in. Ensure UserProvider has received a userProfile.');
    this.name = 'UserNotLoggedInError';
  }
}

/** The category of a failed backend call, used for branching and messaging. */
export type ServiceCallErrorKind = 'client' | 'server' | 'network' | 'unknown';

/** A backend call failed; carries a kind for branching and a user-facing message. */
export class ServiceCallError extends Error {
  constructor(
    readonly kind: ServiceCallErrorKind,
    message: string,
    readonly status: number | undefined,
    readonly cause: unknown,
  ) {
    super(message);
    this.name = 'ServiceCallError';
  }

  /**
   * True when the caller sent too many requests or hit a usage limit, such as starting a quality
   * check too soon after the last one; waiting and retrying later can succeed.
   */
  get isThrottlingError(): boolean {
    return this.status === 429;
  }

  /**
   * True when the backend refused the request as invalid: a field is malformed or out of range,
   * or the action does not fit the item's current state, such as sending locked content for review.
   */
  get isValidationError(): boolean {
    return this.status === 400;
  }

  /**
   * True when the caller's sign-in expired and could not be renewed, or a service behind the
   * backend rejected the backend's own credentials.
   */
  get isAuthenticationError(): boolean {
    return this.status === 401;
  }

  /**
   * True when the caller is signed in but lacks the role or permission the action needs, such as a
   * non-owner changing the content audience.
   */
  get isAccessDeniedError(): boolean {
    return this.status === 403;
  }

  /** True when an item the call names, such as the content, workflow, or user, does not exist. */
  get isNotFoundError(): boolean {
    return this.status === 404;
  }

  /**
   * True when the action no longer applies because the item already changed, such as resolving an
   * inline edit that is already resolved.
   */
  get isConflictError(): boolean {
    return this.status === 409;
  }

  /**
   * True when the backend, or a service it depends on, failed or was unavailable; the caller's
   * request was fine, so retrying later can succeed.
   */
  get isServerError(): boolean {
    return this.kind === 'server';
  }

  /** True when no response came back, because the network failed or the request timed out. */
  get isNetworkError(): boolean {
    return this.kind === 'network';
  }

  /**
   * Converts any thrown value (typically an axios error) into a
   * `ServiceCallError`. Prefers the server's response message (suffixed with its
   * status code) and otherwise derives one from the classified kind and status.
   */
  static from(error: unknown): ServiceCallError {
    // Already classified upstream — pass through unchanged.
    if (error instanceof ServiceCallError) {
      return error;
    }

    // A non-axios throw (serialization bug, unexpected) is unclassifiable.
    if (!axios.isAxiosError(error)) {
      return new ServiceCallError(
        'unknown',
        ServiceCallError.reasonFor('unknown', undefined),
        undefined,
        error,
      );
    }

    const status = error.response?.status;
    const kind = ServiceCallError.classifyKind(status, Boolean(error.request));
    const bodyMessage = ServiceCallError.extractResponseMessage(error.response?.data);
    const message =
      bodyMessage !== undefined
        ? `${bodyMessage} (Code: ${status})`
        : ServiceCallError.reasonFor(kind, status);
    return new ServiceCallError(kind, message, status, error);
  }

  // Classify an axios failure: HTTP status class, else network vs. pre-request.
  private static classifyKind(
    status: number | undefined,
    hasRequest: boolean,
  ): ServiceCallErrorKind {
    if (typeof status === 'number') {
      if (status >= 400 && status < 500) return 'client';
      if (status >= 500 && status < 600) return 'server';
      return 'unknown';
    }
    return hasRequest ? 'network' : 'unknown';
  }

  // The derived reason sentence, preferring a status-specific message.
  private static reasonFor(kind: ServiceCallErrorKind, status: number | undefined): string {
    switch (status) {
      case 400:
        return 'The request was invalid. Please review and try again.';
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return "You don't have access.";
      case 404:
        return 'It was not found.';
    }
    switch (kind) {
      case 'server':
        return 'The service is temporarily unavailable. Please try again shortly.';
      case 'network':
        return 'The request could not be sent. Check your connection and try again.';
      case 'client':
        return 'The request could not be completed. Please review and try again.';
      default:
        return 'An unexpected error occurred. Please report a bug.';
    }
  }

  // A server response body may carry a `message` string we prefer over the default.
  private static extractResponseMessage(data: unknown): string | undefined {
    if (typeof data === 'object' && data !== null && 'message' in data) {
      const message = (data as { message: unknown }).message;
      return typeof message === 'string' && message.length > 0 ? message : undefined;
    }
    return undefined;
  }
}

/**
 * Thrown when uploading or loading an attachment fails, so the attachments UI
 * can message the specific operation.
 */
export class AttachmentTransferError extends Error {
  constructor(
    readonly operation: 'upload' | 'load',
    readonly cause: unknown,
    message = operation === 'upload'
      ? 'Failed to upload the attachment.'
      : 'Failed to load the attachment.',
  ) {
    super(message);
    this.name = 'AttachmentTransferError';
  }

  /** Build from a classified backend failure, composing the operation with why it failed. */
  static fromServiceCall(
    operation: 'upload' | 'load',
    cause: ServiceCallError,
  ): AttachmentTransferError {
    const message =
      operation === 'upload' ? 'Failed to upload the attachment' : 'Failed to load the attachment';
    return new AttachmentTransferError(operation, cause, `${message}: ${cause.message}`);
  }
}

/** A single content validation problem, shaped for display as `key: value`. */
export interface ContentValidationIssue {
  readonly key: string;
  readonly value: string;
}

/**
 * Thrown by a save when the content fails pre-save validation (metadata,
 * template, or links), the way the legacy editor's `beforeSaving` gate did.
 * Carries the flattened issue list so callers can surface a detailed message.
 */
export class ContentValidationError extends Error {
  constructor(readonly issues: readonly ContentValidationIssue[]) {
    super('There are validation issues with your content that must be fixed before saving.');
    this.name = 'ContentValidationError';
  }
}
